import React, { useState, useEffect } from 'react';
import { usersAPI, projectsAPI, tasksAPI, accountingAPI } from '../../services/api';
import { StatCard } from '../../components/common/StatCard';
import { PageHeader } from '../../components/layout/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { UsersIcon, FolderIcon, CheckCircleIcon, CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ users:[], projects:[], tasks:[], fin:null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([usersAPI.getStats(), projectsAPI.getStats(), tasksAPI.getStats(), accountingAPI.getSummary()])
      .then(([u,p,t,f]) => setData({
        users:    u.status==='fulfilled' ? (u.value.data||[]) : [],
        projects: p.status==='fulfilled' ? (p.value.data||[]) : [],
        tasks:    t.status==='fulfilled' ? (t.value.data||[]) : [],
        fin:      f.status==='fulfilled' ? f.value.data : null,
      }))
      .finally(() => setLoading(false));
  }, []);

  const totalUsers    = data.users.reduce((s,u)=>s+u.count,0);
  const totalProjects = data.projects.reduce((s,p)=>s+p.count,0);
  const doneTasks     = (data.tasks.find(t=>t._id==='done')||{}).count || 0;
  const totalTasks    = data.tasks.reduce((s,t)=>s+t.count,0);
  const revenue       = data.fin?.revenue || 0;
  const COLORS        = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6'];

  return (
    <div className="space-y-6">
      <PageHeader title={'Good day, ' + (user?.name?.split(' ')[0]||'') + ' 👋'} subtitle="Here is your organization overview"/>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Users"    value={loading?'...':totalUsers}                    icon={UsersIcon}         color="indigo"/>
        <StatCard title="Projects"       value={loading?'...':totalProjects}                 icon={FolderIcon}        color="blue"/>
        <StatCard title="Tasks Done"     value={loading?'...':(doneTasks+'/'+totalTasks)}    icon={CheckCircleIcon}   color="green"/>
        <StatCard title="Revenue"        value={loading?'...':('₹'+(revenue/1000).toFixed(0)+'K')} icon={CurrencyRupeeIcon} color="amber"/>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 text-sm">Projects by Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.projects.map(p=>({name:p._id,count:p.count}))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="name" tick={{fontSize:11}}/>
              <YAxis tick={{fontSize:11}}/>
              <Tooltip/>
              <Bar dataKey="count" fill="#6366f1" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 text-sm">Tasks by Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.tasks.map(t=>({name:t._id,value:t.count}))} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({name,percent})=>name+' '+Math.round(percent*100)+'%'}>
                {data.tasks.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Tooltip/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}